import { detect as detectBrowser } from 'detect-browser';
import * as config from 'config';
import { LogLevel } from 'types/log-level';
import { getDatabase } from 'database';
import { Guid } from 'types/guid';
import { ApplicationLog } from 'types/application-log';
import * as tenantService from 'services/tenant-service';
import * as deviceService from 'services/device-service';

export function init() {
  const onerror = 'onerror';
  // tslint:disable-next-line:no-any
  const originalOnError: any = window[onerror];
  window.onerror = (message, url, lineNumber, columnNumber, exception) => {
      const handled = originalOnError && originalOnError(message, url, lineNumber, columnNumber, exception);
      if (handled !== true) { // handled could be typeof function
        if (exception) {
          error(exception, 'application.uncaught');
        }
      }

      return handled;
  };
}

interface PartialLog {
  eventName: string;
  codeLocation: string;
  severity: string;
  message: string;
  stackTrace: string;
  epc: string;
}

async function sendLogEntry(partialLogEntry: PartialLog): Promise<void> {
  const deviceId = await deviceService.getDeviceId();
  const deviceIdString = deviceId
    ? deviceId.toString()
    : '';
  const tenantId = await tenantService.getTenantId();

  const browser = detectBrowser();

  // spread operator is equivalent to Object.assign
  const logEntry: ApplicationLog = {
    ... partialLogEntry,
    id: Guid.newGuid(),
    tenantId: tenantId ? tenantId.toString() : '',
    deviceId: deviceIdString,
    timestamp: new Date(),
    browser: browser ? `${browser.name} ${browser.version}` : '',
    operatingSystem: browser ? `${browser.os}` : ''
  };

  const db = await getDatabase();
  await db.add(db.config.indexes.ApplicationLog, logEntry, logEntry.id.toString());
}

export async function log(level: LogLevel, message: string, logLocation: string, eventName: string = ''): Promise<void> {
  try {
    if (config.SEND_LOGS_TO_SERVER) {
      await sendLogEntry({
        severity: level,
        message: message,
        codeLocation: logLocation,
        eventName: eventName,
        stackTrace: '',
        epc: ''
      });
    } else {
      console.log(message);
    }
  } catch (e) {
    console.log('could not send log to server');
    console.log(message);
  }
}

export async function error(exception: Error, logLocation: string, epcRawCode: string = ''): Promise<void> {
  try {
    if (config.SEND_LOGS_TO_SERVER) {
      await sendLogEntry({
        severity: LogLevel.ERROR,
        message: exception.message,
        codeLocation: logLocation,
        eventName: '',
        stackTrace: exception.stack ? exception.stack : '',
        epc: epcRawCode
      });
    } else {
      console.log(exception);
    }
  } catch (e) {
    console.log('could not send log to server');
    console.log(exception);
  }
}

// example below for if we want to track page view
// AppInsights.trackPageView(
//   "FirstPage", /* (optional) page name */
//   null, /* (optional) page url if available */
//   { prop1: "prop1", prop2: "prop2" }, /* (optional) dimension dictionary */
//   { measurement1: 1 }, /* (optional) metric dictionary */
//   123 /* page view duration in milliseconds */
// );

// example below for track event
// AppInsights.trackEvent("TestEvent", { prop1: "prop1", prop2: "prop2" }, { measurement1: 1 });



// WEBPACK FOOTER //
// ./src/logger.ts